<script setup>
/**
 * Connections — the systems the assistant may reach into mid-chat. THE PLAN, §4 P8.
 *
 * THE BRIEF FOR THIS PIECE SETS THE BAR AS A SENTENCE: *an owner who never opens it must
 * lose nothing.* Two things on this page are shaped entirely by that.
 *
 *  • THE EXPLANATION SAYS "MOST BUSINESSES NEVER ADD ONE" IN ITS FIRST BREATH. The failure
 *    mode of an advanced screen is not that it is hard to use; it is that its existence
 *    makes somebody believe she has skipped a setup step. Naming the page as the advanced
 *    corner, in the copy, is the cheapest possible fix and the only one that works for a
 *    person who arrived here by mis-clicking the sidebar.
 *
 *  • THE EMPTY STATE IS A STATEMENT, NOT A PROMPT. "You have none, and that is the normal
 *    answer" — with the primary action reworded to "Add one anyway". `WasloPageLayout`'s
 *    `isEmpty` branch is deliberately NOT used, for the same reason `TasksPage` refuses it:
 *    that branch replaces the body slot, which would delete the paragraph explaining what a
 *    connection is at the exact moment it is the only thing worth reading on the screen.
 *
 * The row switch writes on its own with `{ enabled }` and nothing else, exactly like
 * `TaskCard`'s, and is safe for the same reason: the BFF's PATCH is a genuine partial, so a
 * switch cannot carry a stale copy of a form somebody has open in another tab.
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import WasloExplainer from '../../components/WasloExplainer.vue';
import WasloPageLayout from '../../components/WasloPageLayout.vue';
import RecentlyRemoved from '../../components/RecentlyRemoved.vue';
import ClaudeChip from '../../components/ClaudeChip.vue';
import ConnectedAppsCard from '../../components/ConnectedAppsCard.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { relativeTime } from '../../helpers/relative-time.js';
import { purposeOf } from '../../helpers/connection-draft.js';
import { connectionHealth } from '../../helpers/connection-health.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { wasloApi } from '../../api/client.js';
import { draftedSets } from '../../helpers/claude.js';

const { t, locale } = useWasloI18n();
const router = useRouter();
const accountId = useWasloAccountId();

const connections = useWasloApi(
  signal => wasloApi.getConnections(accountId.value, signal),
  { isEmpty: () => false }
);

/**
 * P-10.21: how many ON tasks use each connection, for the switch-off confirm. A secondary
 * read: when it fails the switch works as it always did, with no confirm to show.
 */
const connectionUse = useWasloApi(
  signal => wasloApi.getConnectionUse(accountId.value, signal),
  { isEmpty: () => false }
);
const usedBy = connection =>
  connectionUse.data.value?.inUse?.[connection.name] ?? 0;

/** E2 (P-8.4): connections Claude drafted that nobody has touched. A failed read shows no chip. */
const claudeSummary = useWasloApi(
  signal => wasloApi.getAssistantSummary(accountId.value, signal),
  { isEmpty: () => false }
);
const drafted = computed(() => draftedSets(claudeSummary.data.value));

const isFetching = connections.isFetching;

const errorMessage = computed(() => connections.errorMessage.value);

const rows = computed(() => connections.data.value ?? []);

const newUrl = computed(() =>
  accountId.value === null
    ? null
    : frontendURL(`accounts/${accountId.value}/waslo/settings/connections/new`)
);

const editUrl = id =>
  accountId.value === null
    ? null
    : frontendURL(
        `accounts/${accountId.value}/waslo/settings/connections/${id}`
      );

const openNew = () => {
  if (newUrl.value) router.push(newUrl.value);
};

const changedLabel = connection => {
  const when = relativeTime(connection.updatedAt, locale.value, {
    justNow: t('WASLO.SETTINGS.CONNECTIONS.LIST.UPDATED_JUST_NOW'),
  });
  return when ? t('WASLO.SETTINGS.CONNECTIONS.LIST.UPDATED', { when }) : '';
};

/** An absent stamp (older BFF) is "unknown", never "untested" (P-3.4). */
const untested = connection => connection.lastTestOkAt === null;

/** P-3.8: a sign-in with nothing stored behind it. It cannot be switched on until one is saved. */
const needsSignIn = connection => connection.signInNeeded === true;

/** An OFF row that is untested, or has no sign-in behind it, cannot be switched on. */
const cannotSwitchOn = connection =>
  !connection.enabled && (untested(connection) || needsSignIn(connection));

/** P-3.5: the week of live use under the row, in the owner's words. */
const health = connection => connectionHealth(connection, t, locale.value);

const purposeWord = connection => {
  const purpose = purposeOf(connection);
  if (purpose === 'lookup') {
    return t('WASLO.SETTINGS.CONNECTIONS.LIST.PURPOSE_LOOKUP');
  }
  if (purpose === 'change') {
    return t('WASLO.SETTINGS.CONNECTIONS.LIST.PURPOSE_CHANGE');
  }
  return '';
};

/** The row's second line, joined here rather than in the template — see the comment there. */
const metaLine = connection =>
  [
    purposeWord(connection),
    connection.signInSaved
      ? t('WASLO.SETTINGS.CONNECTIONS.LIST.SIGN_IN_SAVED')
      : '',
    needsSignIn(connection)
      ? t('WASLO.SETTINGS.CONNECTIONS.LIST.SIGN_IN_NEEDED')
      : '',
    connection.enabled && untested(connection)
      ? t('WASLO.SETTINGS.CONNECTIONS.LIST.NOT_TRIED')
      : '',
    changedLabel(connection),
  ]
    .filter(Boolean)
    .join(' · ');

// ── the row switches ───────────────────────────────────────────────────────────────
// One id at a time, not one boolean for the page — `TasksPage` writes the long version.
const savingId = ref(null);
const saveError = ref('');
const switchEpoch = ref(0);

/** The row whose switch-off is waiting on "Switch it off", or `null`. */
const confirmingOff = ref(null);

const confirmLine = connection => {
  const count = usedBy(connection);
  return t(
    'WASLO.SETTINGS.CONNECTIONS.LIST.OFF_CONFIRM',
    { count: new Intl.NumberFormat(toIntlLocale(locale.value)).format(count) },
    count
  );
};

const onToggle = async (connection, next) => {
  // P-3.4 / P-3.8: the BFF refuses an OFF-to-ON change of an untested row or of a sign-in with
  // nothing behind it, so it is never sent.
  if (next && cannotSwitchOn(connection)) return;
  if (savingId.value !== null) return;
  // P-10.21: switching off what an ON task uses asks first. The switch snaps back until then.
  if (
    !next &&
    usedBy(connection) > 0 &&
    confirmingOff.value !== connection.id
  ) {
    confirmingOff.value = connection.id;
    switchEpoch.value += 1;
    return;
  }
  confirmingOff.value = null;
  savingId.value = connection.id;
  saveError.value = '';
  try {
    const saved = await wasloApi.updateConnection(
      accountId.value,
      connection.id,
      { enabled: next }
    );
    // A write answers with the DETAIL, which carries no week of use: the row keeps its own.
    connections.data.value = rows.value.map(row =>
      row.id === saved.id ? { ...row, ...saved, health: row.health } : row
    );
  } catch {
    saveError.value = t('WASLO.SETTINGS.CONNECTIONS.LIST.SAVE_FAILED');
    switchEpoch.value += 1;
  } finally {
    savingId.value = null;
  }
};

/**
 * Field report F1.6: a connection open chat may use (a lookup, or a change kept there for now)
 * can be kept for tasks only. Shown on a row marked so, too, so the owner can undo it.
 */
const chatUsable = connection =>
  connection.taskOnly === true ||
  connection.legacyChatUse === true ||
  purposeOf(connection) === 'lookup';

const setTaskOnly = async (connection, on) => {
  if (savingId.value !== null) return;
  savingId.value = connection.id;
  saveError.value = '';
  try {
    const saved = await wasloApi.updateConnection(
      accountId.value,
      connection.id,
      { taskOnly: on }
    );
    connections.data.value = rows.value.map(row =>
      row.id === saved.id ? { ...row, ...saved, health: row.health } : row
    );
  } catch {
    saveError.value = t('WASLO.SETTINGS.CONNECTIONS.LIST.SAVE_FAILED');
  } finally {
    savingId.value = null;
  }
};

/** P-3.4 / P-10.4: take a connection out of open chat. It can only clear, never set. */
const stopChatUse = async connection => {
  if (savingId.value !== null) return;
  savingId.value = connection.id;
  saveError.value = '';
  try {
    const saved = await wasloApi.updateConnection(
      accountId.value,
      connection.id,
      { stopChatUse: true }
    );
    connections.data.value = rows.value.map(row =>
      row.id === saved.id
        ? { ...row, ...saved, legacyChatUse: false, health: row.health }
        : row
    );
  } catch {
    saveError.value = t('WASLO.SETTINGS.CONNECTIONS.LIST.SAVE_FAILED');
  } finally {
    savingId.value = null;
  }
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.SETTINGS.CONNECTIONS.TITLE')"
    :is-fetching="isFetching"
    :not-provisioned="connections.isNotProvisioned.value"
    :error-message="errorMessage"
    :button-label="rows.length ? t('WASLO.SETTINGS.CONNECTIONS.ADD') : ''"
    @click="openNew"
    @retry="connections.reload()"
  >
    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <WasloExplainer
          icon="i-lucide-plug"
          :note="t('WASLO.SETTINGS.CONNECTIONS.SPOTLIGHT')"
        />

        <section class="flex flex-col gap-4">
          <!-- The primary "add" lives in the page header, the same as Answers, Tasks and
               Broadcasts; when the list is empty it is suppressed there and the empty-state
               card below carries it instead. -->
          <SettingsHeader
            :heading="t('WASLO.SETTINGS.CONNECTIONS.HEADING')"
            :description="t('WASLO.SETTINGS.CONNECTIONS.DESCRIPTION')"
          />

          <Banner v-if="saveError" color="ruby" data-waslo-connections-error>
            {{ saveError }}
          </Banner>

          <ul
            v-if="rows.length"
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-connection-list
          >
            <li
              v-for="connection in rows"
              :key="connection.id"
              class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
              :data-waslo-connection="connection.id"
            >
              <span
                class="grid rounded-lg shrink-0 place-items-center size-9"
                :class="connection.enabled ? 'bg-n-blue-3' : 'bg-n-alpha-2'"
                aria-hidden="true"
              >
                <span
                  class="size-4 i-lucide-plug"
                  :class="
                    connection.enabled ? 'text-n-blue-10' : 'text-n-slate-10'
                  "
                />
              </span>

              <div class="flex flex-col min-w-0 gap-1">
                <!-- The owner's own sentence, in an inline isolate so an Arabic
                     description cannot swap punctuation with the row around it. -->
                <p
                  class="mb-0 text-sm font-medium break-words text-n-slate-12"
                  data-waslo-connection-description
                >
                  <bdi>{{ connection.description }}</bdi>
                </p>
                <ClaudeChip v-if="drafted.connections.has(connection.id)" />
                <!-- The short name is a machine identifier: always left to right, and
                     always shown, because it is what a task attaches by and a mis-set
                     connection is unidentifiable without it. -->
                <p class="mb-0 text-xs text-n-slate-10" dir="ltr">
                  {{
                    t('WASLO.SETTINGS.CONNECTIONS.LIST.NAME', {
                      name: connection.name,
                    })
                  }}
                </p>
                <!--
                  ONE STRING, ONE NODE, ONE DIRECTION. Three inline boxes joined by a
                  literal separator is three things the bidi algorithm can reorder
                  independently, and the separator itself is a neutral that gets absorbed
                  by whichever run touches it. `TaskCard` writes the same line for the
                  same reason.
                -->
                <p
                  v-if="metaLine(connection)"
                  class="mb-0 text-xs text-n-slate-10"
                  dir="auto"
                  data-waslo-connection-meta
                >
                  {{ metaLine(connection) }}
                </p>
                <!-- P-3.5: the week of live use, one line. A failing one gets a dot and the
                     reason in the owner's words; no line at all when the BFF sent none. -->
                <p
                  v-if="health(connection).line"
                  class="flex items-center gap-2 mb-0 text-xs"
                  :class="
                    health(connection).failing
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-10'
                  "
                  dir="auto"
                  data-waslo-connection-health
                  :data-waslo-connection-health-state="connection.health?.state"
                >
                  <span
                    v-if="health(connection).failing"
                    class="rounded-full shrink-0 size-2 bg-n-ruby-9"
                    aria-hidden="true"
                    data-waslo-connection-failing-dot
                  />
                  {{ health(connection).line }}
                </p>
                <p
                  v-if="health(connection).reason"
                  class="mb-0 text-xs text-n-ruby-11"
                  dir="auto"
                  data-waslo-connection-health-reason
                >
                  {{ health(connection).reason }}
                </p>
                <!-- F1.6: "Only inside tasks". -->
                <label
                  v-if="chatUsable(connection)"
                  class="flex items-start gap-2 text-xs cursor-pointer text-n-slate-11"
                  data-waslo-connection-task-only
                >
                  <Checkbox
                    class="mt-0.5 shrink-0"
                    :model-value="connection.taskOnly === true"
                    :disabled="savingId !== null"
                    @update:model-value="on => setTaskOnly(connection, on)"
                  />
                  <span class="flex flex-col gap-0.5">
                    <span class="text-n-slate-12">{{
                      t('WASLO.SETTINGS.CONNECTIONS.LIST.TASK_ONLY')
                    }}</span>
                    <span>{{
                      t('WASLO.SETTINGS.CONNECTIONS.LIST.TASK_ONLY_HINT')
                    }}</span>
                  </span>
                </label>
                <div
                  v-if="connection.legacyChatUse"
                  class="flex flex-wrap items-center gap-2"
                  data-waslo-connection-legacy
                >
                  <p class="mb-0 text-xs text-n-amber-11">
                    {{ t('WASLO.SETTINGS.CONNECTIONS.LIST.LEGACY') }}
                  </p>
                  <Button
                    :label="t('WASLO.SETTINGS.CONNECTIONS.LIST.LEGACY_STOP')"
                    variant="faded"
                    color="slate"
                    size="xs"
                    :disabled="savingId !== null"
                    data-waslo-connection-legacy-stop
                    @click="stopChatUse(connection)"
                  />
                </div>
                <div
                  v-if="confirmingOff === connection.id"
                  class="flex flex-wrap items-center gap-2"
                  role="alert"
                  data-waslo-connection-off-confirm
                >
                  <p class="mb-0 text-xs text-n-amber-11">
                    {{ confirmLine(connection) }}
                  </p>
                  <Button
                    :label="t('WASLO.SETTINGS.CONNECTIONS.LIST.OFF_YES')"
                    color="ruby"
                    size="xs"
                    data-waslo-connection-off-yes
                    @click="onToggle(connection, false)"
                  />
                  <Button
                    :label="t('WASLO.SETTINGS.CONNECTIONS.LIST.OFF_NO')"
                    variant="faded"
                    color="slate"
                    size="xs"
                    data-waslo-connection-off-no
                    @click="confirmingOff = null"
                  />
                </div>
              </div>

              <!-- The same slot, in the same order, on every row. -->
              <div class="flex items-center gap-3 shrink-0 sm:ms-auto ms-0">
                <span
                  class="text-sm text-n-slate-11"
                  data-waslo-connection-state
                >
                  {{
                    savingId === connection.id
                      ? t('WASLO.PERSONALITY.PERMISSIONS.SAVING')
                      : connection.enabled
                        ? t('WASLO.SETTINGS.CONNECTIONS.LIST.ON')
                        : t('WASLO.SETTINGS.CONNECTIONS.LIST.OFF')
                  }}
                </span>
                <span
                  v-if="!connection.enabled && needsSignIn(connection)"
                  class="text-xs text-n-amber-11"
                  data-waslo-connection-sign-in-needed
                >
                  {{ t('WASLO.SETTINGS.CONNECTIONS.LIST.SIGN_IN_NEEDED') }}
                </span>
                <span
                  v-else-if="!connection.enabled && untested(connection)"
                  class="text-xs text-n-amber-11"
                  data-waslo-connection-try-first
                >
                  {{ t('WASLO.SETTINGS.CONNECTIONS.LIST.TRY_FIRST') }}
                </span>
                <Switch
                  :key="switchEpoch"
                  :model-value="connection.enabled"
                  :disabled="cannotSwitchOn(connection)"
                  :aria-label="connection.description || connection.name"
                  data-waslo-connection-switch
                  @update:model-value="next => onToggle(connection, next)"
                />
                <router-link
                  v-if="editUrl(connection.id)"
                  :to="editUrl(connection.id)"
                  class="inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
                  data-waslo-connection-open
                >
                  {{ t('WASLO.SETTINGS.CONNECTIONS.LIST.OPEN') }}
                </router-link>
              </div>
            </li>
          </ul>

          <!--
            The empty state, inline and under the explanation rather than instead of it —
            and phrased as an answer rather than as a task nobody has done yet.
          -->
          <div
            v-else
            class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-connections-empty
          >
            <span
              class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-alpha-2"
              aria-hidden="true"
            >
              <span class="size-5 i-lucide-plug text-n-slate-10" />
            </span>
            <div class="flex flex-col items-start gap-3">
              <div class="flex flex-col gap-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t('WASLO.SETTINGS.CONNECTIONS.EMPTY.TITLE') }}
                </p>
                <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                  {{ t('WASLO.SETTINGS.CONNECTIONS.EMPTY.BODY') }}
                </p>
              </div>
              <Button
                :label="t('WASLO.SETTINGS.CONNECTIONS.EMPTY.ACTION')"
                icon="i-lucide-plus"
                variant="faded"
                color="slate"
                size="sm"
                data-waslo-connection-add-first
                @click="openNew"
              />
            </div>
          </div>
        </section>

        <!-- E2 (P-8.4): the AI apps she connected, and how to add Waslo to her own Claude. -->
        <ConnectedAppsCard :account-id="accountId" />

        <!-- P-6.11: connections removed in the last 30 days. They come back switched off. -->
        <RecentlyRemoved
          :account-id="accountId"
          :kinds="['connection']"
          @restored="connections.reload()"
        />
      </div>
    </template>
  </WasloPageLayout>
</template>
